import { FlaskConical, GitBranch } from "lucide-react";
import { useTranslation } from "@ncp/i18n";
import { Badge, Field, NativeSelect, StatCard } from "@ncp/ui";
import { CatalogPage } from "../features/generic/CatalogPage";
import { RULE_KINDS, newRuleBody, type RuleBody, type RuleSummary } from "../features/rules/api";

/** Rules: the conditions workflows, services and automations check – decision tables or expressions, each with its own test cases. */
export function RulesPage() {
  const { t } = useTranslation();
  return (
    <CatalogPage<RuleSummary, RuleBody>
      resource="rules"
      basePath="/rules"
      icon={GitBranch}
      title={t("admin:rules.title")}
      subtitle={t("admin:rules.subtitle")}
      navLabel={t("admin:nav.rules")}
      writePermission="platform.rules.write"
      newLabel={t("admin:rules.new")}
      emptyTitle={t("admin:rules.emptyTitle")}
      emptyBody={t("admin:rules.emptyBody")}
      docsAnchor="rules"
      help={<><p>{t("admin:rules.help.what")}</p><p>{t("admin:rules.help.table")}</p><p>{t("admin:rules.help.tests")}</p></>}
      newBody={(name, nameAr, extra) => newRuleBody(name, nameAr, extra)}
      createFields={(extra, set) => (
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={t("admin:rules.fields.kind")}>
            <NativeSelect data-testid="create-kind" value={extra.kind ?? "Guard"} onChange={(e) => set("kind", e.target.value)}>
              {RULE_KINDS.map((k) => <option key={k} value={k}>{t(`admin:rules.kinds.${k}`)}</option>)}
            </NativeSelect>
          </Field>
          <Field label={t("admin:rules.fields.mode")}>
            <NativeSelect data-testid="create-mode" value={extra.mode ?? "table"} onChange={(e) => set("mode", e.target.value)}>
              <option value="table">{t("admin:rules.modes.table")}</option>
              <option value="logic">{t("admin:rules.modes.logic")}</option>
            </NativeSelect>
          </Field>
        </div>
      )}
      stats={(rows) => {
        const tested = rows.filter((r) => r.lastTestTotal);
        return <StatCard label={t("admin:rules.stats.tested")} value={tested.filter((r) => r.lastTestPassed === r.lastTestTotal).length} icon={FlaskConical} tone="neutral" footer={t("admin:rules.stats.testedHint")} />;
      }}
      columns={[
        { header: t("admin:rules.columns.kind"), cell: (r) => (r.kind ? <Badge variant="outline" size="sm">{t(`admin:rules.kinds.${r.kind}`)}</Badge> : null) },
        { header: t("admin:rules.columns.form"), cell: (r) => (r.isTable ? t("admin:rules.modes.table") : t("admin:rules.modes.logic")) },
        { header: t("admin:rules.columns.tests"), cell: (r) => r.lastTestTotal ? <Badge variant={r.lastTestPassed === r.lastTestTotal ? "success" : "danger"} size="sm">{r.lastTestPassed}/{r.lastTestTotal}</Badge> : <span className="text-muted-foreground">—</span> },
        { header: t("admin:rules.columns.service"), cell: (r) => r.serviceKey ? <code className="font-mono">{r.serviceKey}</code> : <span className="text-muted-foreground">{t("admin:rules.platformWide")}</span> },
      ]}
    />
  );
}
